"use client"

import { useState } from "react"
import { CheckCircle2 } from "lucide-react"

type ContactStatus = "idle" | "submitting" | "success" | "error"

/**
 * The /contact message form. Until the form audit (plan-of-record item 168) this
 * was a bare server-rendered <form> with no handler — submitting did a native
 * GET reload that dropped the message and leaked it into the URL. Now POSTs
 * to /api/contact → contact_messages (the team reads and answers from the
 * table; no auto-email is sent under the decision-#10 NOTHING SENDS gate, so
 * the success copy promises storage, not an instant email).
 */
export function ContactForm() {
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [subject, setSubject] = useState("")
  const [message, setMessage] = useState("")
  const [honeypot, setHoneypot] = useState("")
  const [status, setStatus] = useState<ContactStatus>("idle")
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus("submitting")
    setErrorMessage(null)
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ firstName, lastName, email, subject, message, honeypot }),
      })
      const data = await res.json().catch(() => null)
      if (res.ok && data?.ok) {
        setStatus("success")
      } else {
        setStatus("error")
        setErrorMessage(
          typeof data?.error === "string" && data.error.includes(" ")
            ? data.error
            : "Something went wrong and your message was not sent. Please try again.",
        )
      }
    } catch {
      setStatus("error")
      setErrorMessage("Something went wrong and your message was not sent. Please try again.")
    }
  }

  if (status === "success") {
    return (
      <div
        className="rounded-lg border border-gray-200 bg-gray-50 p-5 flex items-start gap-3"
        data-testid="contact-form"
        data-state="success"
      >
        <CheckCircle2 className="w-5 h-5 text-[#10a65c] mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold text-gray-900 text-sm">Message received.</p>
          <p className="text-sm text-gray-600 mt-1">
            Your message is saved and in the team&apos;s queue. If it needs a reply, we&apos;ll
            email you at {email}.
          </p>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
            First Name *
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            required
            maxLength={80}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ff9951]/20 focus:border-[#ff9951] transition-colors"
          />
        </div>
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
            Last Name *
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            required
            maxLength={80}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ff9951]/20 focus:border-[#ff9951] transition-colors"
          />
        </div>
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          Email Address *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          maxLength={320}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ff9951]/20 focus:border-[#ff9951] transition-colors"
        />
      </div>

      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
          Subject *
        </label>
        <select
          id="subject"
          name="subject"
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ff9951]/20 focus:border-[#ff9951] transition-colors"
        >
          <option value="">Select a subject</option>
          <option value="partnership">Partnership, press or media inquiry</option>
          <option value="technical-issue">Technical issue with website</option>
          <option value="product-recommendation">Product recommendation request</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
          Message *
        </label>
        <textarea
          id="message"
          name="message"
          rows={6}
          required
          maxLength={5000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Tell us how we can help you..."
          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ff9951]/20 focus:border-[#ff9951] transition-colors"
        ></textarea>
      </div>

      {/* Honeypot — hidden from real users; bots that fill it are rejected. */}
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      {status === "error" && errorMessage && (
        <p className="text-sm text-red-600" role="alert">
          {errorMessage}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] font-semibold py-3 rounded-md transition-colors disabled:opacity-100 disabled:cursor-not-allowed"
      >
        {status === "submitting" ? "Sending…" : "Send Message"}
      </button>
    </form>
  )
}
